import mime from 'mime';
import { saveAs } from 'file-saver';
import { downloadZip } from 'client-zip';
import { fetchUploads } from '@/modules/uploads/api';
// Type-only, so this stays a compile-time reference and the api module can import
// the pipeline back without a runtime cycle.
import type { ClientDocumentDownload } from './api';

// The backend accepts up to 50 ids per request. Asking for fewer keeps the oldest
// URL in a chunk well inside the uploads service's five-minute signature: the zip
// pulls entries one at a time, so requesting every URL up front would expire the
// tail of a large selection before the archive reached it.
const CHUNK_SIZE = 25;

// Caps on the total bytes in one archive, checked before anything is fetched.
// The streaming path writes straight to disk so memory stays flat, while the
// fallback collects the whole archive in a Blob first, so it gets less headroom.
const MAX_STREAMED_BYTES = 2 * 1024 ** 3;
const MAX_BUFFERED_BYTES = 500 * 1024 ** 2;

export type DownloadableClientDocument = {
  id: string;
  title: string;
  uploadID: string;
};

// Passed in rather than imported so the chunking behaviour can be tested without
// the network.
export type FetchDownloadURLs = (
  ids: string[],
) => Promise<{ data: { documents: ClientDocumentDownload[] } }>;

// A document left out of the archive, so the caller can tell the user which and
// why rather than handing them a silently incomplete zip.
export type OmittedDocument = {
  title: string;
  reason: ClientDocumentDownload['unavailable'] | 'fetch_failed';
};

export class SelectionTooLargeError extends Error {
  constructor(readonly maxBytes: number) {
    super('selection exceeds the archive size limit');
    this.name = 'SelectionTooLargeError';
  }
}

// Entry names come from user-supplied upload filenames and client-zip writes
// whatever it is given, so a name containing `../` would be a zip-slip bug in
// whatever tool extracts the archive. A file with no recorded name falls back to
// the document's title plus the extension its content type implies, the same
// inference the table's Type column makes.
export function safeEntryName(document: DownloadableClientDocument, file: ClientDocumentDownload) {
  const extension = file.contentType ? mime.getExtension(file.contentType) : null;
  const named = extension ? `${document.title}.${extension}` : document.title;
  const flattened = (file.filename || named).replaceAll(/[/\\]/g, '_').replace(/^\.+/, '');
  return flattened || document.title;
}

// Two documents can legitimately hold files of the same name. Windows and macOS
// treat `Policy.pdf` and `policy.pdf` as a collision on extraction even though the
// archive can hold both, so the comparison ignores case.
export function nameDeduplicator() {
  const taken = new Set<string>();

  return function unique(name: string) {
    if (!taken.has(name.toLowerCase())) {
      taken.add(name.toLowerCase());
      return name;
    }

    const dot = name.lastIndexOf('.');
    const [stem, extension] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];

    for (let n = 2; ; n += 1) {
      const candidate = `${stem} (${n})${extension}`;
      if (!taken.has(candidate.toLowerCase())) {
        taken.add(candidate.toLowerCase());
        return candidate;
      }
    }
  };
}

// Refuses an oversized selection before a single byte is fetched. The table rows
// carry no file size, so the sizes come from the uploads service, which is the
// same batched lookup a single download already uses to name its file.
async function assertWithinSizeCap(documents: DownloadableClientDocument[], maxBytes: number) {
  const uploadIDs = documents.map((document) => document.uploadID).filter(Boolean);
  if (!uploadIDs.length) return;

  const { data } = await fetchUploads(uploadIDs);

  // An unknown size is treated as zero rather than failing the download: the
  // archive is capped on what we can actually measure, and the alternative is
  // refusing a selection that may well be small.
  const total = uploadIDs.reduce((sum, id) => sum + (data.files[id]?.fileSize ?? 0), 0);

  if (total > maxBytes) throw new SelectionTooLargeError(maxBytes);
}

// Yields one zip entry per document, fetching the next chunk of presigned URLs
// only once the zip has drained the previous one. client-zip pulls entries one at
// a time, which is what keeps a URL from being signed long before it is used.
export async function* zipEntries(
  documents: DownloadableClientDocument[],
  omitted: OmittedDocument[],
  fetchURLs: FetchDownloadURLs,
) {
  const byID = new Map(documents.map((document) => [document.id, document]));
  const unique = nameDeduplicator();

  for (let i = 0; i < documents.length; i += CHUNK_SIZE) {
    const chunk = documents.slice(i, i + CHUNK_SIZE);
    // Sequential on purpose: requesting every chunk at once is what would expire
    // the URLs this generator exists to mint late.
    // oxlint-disable-next-line no-await-in-loop
    const { data } = await fetchURLs(chunk.map((document) => document.id));

    for (const file of data.documents) {
      const document = byID.get(file.documentID);
      if (!document) continue;

      if (!file.url) {
        omitted.push({ title: document.title, reason: file.unavailable });
        continue;
      }

      // Also sequential: client-zip writes one entry at a time, so fetching ahead
      // would buffer file bytes this design exists to stream.
      // oxlint-disable-next-line no-await-in-loop
      const response = await fetch(file.url);
      if (!response.ok) {
        omitted.push({ title: document.title, reason: 'fetch_failed' });
        continue;
      }

      yield {
        name: unique(safeEntryName(document, file)),
        // Left unset, every entry would be dated the moment of download, which is
        // misleading rather than merely wrong.
        lastModified: new Date(file.modifiedAt),
        input: response,
      };
    }
  }
}

// Returns the documents that could not be included. Throws
// SelectionTooLargeError when the selection is over the cap, and treats the user
// dismissing the save dialog as a cancellation rather than a failure.
export async function downloadClientDocumentsAsZip(
  documents: DownloadableClientDocument[],
  filename: string,
  fetchURLs: FetchDownloadURLs,
) {
  // Chromium only: Safari and Firefox have no equivalent, and the service-worker
  // trick that emulates it needs a same-origin worker intercepting a synthetic
  // download URL, which interacts badly with our CSP. Bound because the API
  // requires `window` as its receiver.
  const picker = window.showSaveFilePicker?.bind(window);
  await assertWithinSizeCap(documents, picker ? MAX_STREAMED_BYTES : MAX_BUFFERED_BYTES);

  const omitted: OmittedDocument[] = [];

  if (picker) {
    let handle;
    try {
      handle = await picker({
        suggestedName: filename,
        types: [{ description: 'Zip archive', accept: { 'application/zip': ['.zip'] } }],
      });
    } catch (err) {
      // Dismissing the picker is a cancellation, not a failure.
      if (err instanceof DOMException && err.name === 'AbortError') return omitted;
      throw err;
    }

    const zip = downloadZip(zipEntries(documents, omitted, fetchURLs));
    await zip.body?.pipeTo(await handle.createWritable());
    return omitted;
  }

  const zip = downloadZip(zipEntries(documents, omitted, fetchURLs));
  saveAs(await zip.blob(), filename);
  return omitted;
}

export function archiveName(today = new Date()) {
  return `Documents ${today.toISOString().slice(0, 10)}.zip`;
}
